import api from './api';

/** Attachment item for email templates (create/send) */
export interface EmailTemplateAttachment {
  id?: number;
  filename: string;
  file_url: string;
  file_size?: number;
  mime_type?: string;
  is_inline?: boolean;
}

export interface EmailTemplate {
  id: number;
  account_id: number;
  channel_id?: number;
  name: string;
  subject: string;
  body_html: string;
  body_text?: string;
  variables: string[];
  is_active: boolean;
  created_at: string;
  updated_at: string;
  usage_count?: number;
  /** Optional: category (marketing, transactional, support, etc.) */
  category?: string;
  /** Optional: use account/user signature */
  signature_enabled?: boolean;
  /** Optional: custom signature HTML */
  custom_signature?: string;
  /** Optional: attachments */
  attachments?: EmailTemplateAttachment[];
}

export interface EmailTemplateCreate {
  name: string;
  subject: string;
  body_html: string;
  body_text?: string;
  variables: string[];
  channel_id?: number;
  is_active?: boolean;
  category?: string;
  signature_enabled?: boolean;
  custom_signature?: string;
  attachments?: EmailTemplateAttachment[];
}

export interface EmailTemplateUpdate {
  name?: string;
  subject?: string;
  body_html?: string;
  body_text?: string;
  variables?: string[];
  channel_id?: number;
  is_active?: boolean;
  category?: string;
  signature_enabled?: boolean;
  custom_signature?: string;
  attachments?: EmailTemplateAttachment[];
}

export interface EmailTemplateSendRequest {
  conversation_id: number;
  variables: Record<string, string>;
}

const emailTemplatesApi = {
  /**
   * List all email templates
   */
  list: async (activeOnly = true): Promise<EmailTemplate[]> => {
    const response = await api.get('/email/templates/', {
      params: { active_only: activeOnly }
    });
    return response.data;
  },

  /**
   * Get a single email template
   */
  get: async (templateId: number): Promise<EmailTemplate> => {
    const response = await api.get(`/email/templates/${templateId}`);
    return response.data;
  },

  /**
   * Create a new email template
   */
  create: async (data: EmailTemplateCreate): Promise<EmailTemplate> => {
    const response = await api.post('/email/templates/', data);
    return response.data;
  },

  /**
   * Update an email template
   */
  update: async (templateId: number, data: EmailTemplateUpdate): Promise<EmailTemplate> => {
    const response = await api.put(`/email/templates/${templateId}`, data);
    return response.data;
  },

  /**
   * Delete an email template
   */
  delete: async (templateId: number): Promise<void> => {
    await api.delete(`/email/templates/${templateId}`);
  },

  /**
   * Send an email template in a conversation
   */
  send: async (templateId: number, data: EmailTemplateSendRequest): Promise<{ success: boolean; result?: any; error?: string }> => {
    const response = await api.post(`/email/templates/${templateId}/send`, data);
    return response.data;
  },

  /**
   * Preview template with variables
   */
  preview: (template: EmailTemplate, variables: Record<string, string>): { subject: string; body: string } => {
    let subject = template.subject;
    let body = template.body_html;

    for (const [key, value] of Object.entries(variables)) {
      const placeholder = `{{${key}}}`;
      subject = subject.replace(new RegExp(placeholder, 'g'), value);
      body = body.replace(new RegExp(placeholder, 'g'), value);
    }

    return { subject, body };
  }
};

export default emailTemplatesApi;